/**
 * Claim or release, in the session drawer's footer with the other session actions. Claim makes
 * the session yours (the teams owner: its requests come to you, and a session Orgus only watched
 * can be stopped from here); without the teams module it is the session-control claim. Release
 * takes back a session-control claim.
 */
import { Hand, Undo2 } from '@orgus/ui/icons';
import { useState } from 'react';
import { sessionOrigin } from '@orgus/protocol';
import type { Session } from '@orgus/protocol';
import { Button, Tooltip, toast } from '@orgus/ui';
import type { UiError } from '@orgus/ui';
import { toUiError } from '../../lib/api.ts';
import { useControlState } from '../control/live.ts';
import { useClaim, useMe } from '../session-detail/api.ts';
import { teamsClient } from '../teams/client.ts';
import { useTeams } from '../teams/model.ts';
import { claimSession, releaseClaim } from './api.ts';
import { claimAction } from './model.ts';

export function SessionClaimButton({ s, onError }: { s: Session; onError: (e: UiError | null) => void }) {
  const me = useMe();
  const claim = useClaim(s);
  const teams = useTeams();
  const controlled = !!useControlState()?.machines[s.machineId]?.installed;
  const [busy, setBusy] = useState(false);
  const owner = teams.status === 'ready' && teams.data ? (Object.hasOwn(teams.data.sessionOwners, s.id) ? (teams.data.sessionOwners[s.id] ?? '') : '') : null;
  const action = claimAction({ origin: sessionOrigin(s), claim, owner, controlled, me });
  if (action.kind === 'none') return null;

  const run = async () => {
    setBusy(true);
    onError(null);
    try {
      if (action.kind === 'release') {
        await releaseClaim(s);
        toast.success('Claim released', 'Orgus only watches this session again, unless someone owns it or session control is on.');
      } else if (action.via === 'teams') {
        await teamsClient.claim(`s:${s.id}`, s.title, s.projectId);
        toast.success('This session is yours', 'Its requests come to you when no routing rule names anyone else, and you can stop it from here.');
      } else {
        await claimSession(s);
        toast.success('Session claimed', 'You can stop it from here. Nothing was sent into the session.');
      }
    } catch (e) {
      onError(toUiError(e, { what: action.kind === 'release' ? 'The claim was not released.' : 'The session was not claimed.', next: 'Reload the drawer and try again.' }));
    } finally {
      setBusy(false);
    }
  };

  const hint =
    action.blocked ??
    (action.kind === 'release'
      ? 'Orgus goes back to only watching it. Nothing is sent into the session.'
      : sessionOrigin(s) === 'discovered'
        ? 'Orgus only watches this session. Claim it to stop it from here; nothing is sent into it.'
        : 'Nobody owns this session. Claim it so its requests come to you.');
  return (
    <Tooltip content={hint}>
      <span tabIndex={action.blocked ? 0 : -1} className="inline-flex">
        <Button icon={action.kind === 'release' ? <Undo2 /> : <Hand />} loading={busy} disabled={!!action.blocked} onClick={() => void run()}>
          {action.kind === 'release' ? 'Release claim' : 'Claim session'}
        </Button>
      </span>
    </Tooltip>
  );
}
